import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Plus, Edit, Trash2, Power, PowerOff, Search, Filter, X, ExternalLink } from 'lucide-react'
import { multiTemplateGeneratorAPI } from '../../services/api'
import { toUtcDate } from '../../utils/dateFormat'

const MultiTemplateGeneratorPage = () => {
  const navigate = useNavigate()
  const [items, setItems] = useState([])
  const [groups, setGroups] = useState([])
  const [loading, setLoading] = useState(true)
  const [filters, setFilters] = useState({
    group: '',
    is_active: null,
    search: ''
  })
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0
  })
  
  // Add Template Dialog State
  const [showAddTemplateDialog, setShowAddTemplateDialog] = useState(false)
  const [templateType, setTemplateType] = useState('video') // 'video' or 'image'
  const [uploading, setUploading] = useState(false)
  const [uploadProgress, setUploadProgress] = useState(0)
  const [uploadStage, setUploadStage] = useState('')
  const [templateForm, setTemplateForm] = useState({
    templateName: '',
    group: '',
    subcategory: '',
    width: '',
    height: '',
    file: null,
    detectedAttributes: null
  })

  useEffect(() => {
    fetchItems()
    fetchGroups()
  }, [pagination.page, filters])

  const fetchItems = async () => {
    try {
      setLoading(true)
      const params = {
        page: pagination.page,
        limit: pagination.limit,
        ...(filters.group && { group: filters.group }),
        ...(filters.is_active !== null && { is_active: filters.is_active })
      }
      
      const response = await multiTemplateGeneratorAPI.getAll(params)
      if (response.data.success) {
        setItems(response.data.data.items)
        setPagination(prev => ({
          ...prev,
          ...response.data.data.pagination
        }))
      }
    } catch (error) {
      console.error('Error fetching items:', error)
      alert('Failed to fetch items')
    } finally {
      setLoading(false)
    }
  }

  const fetchGroups = async () => {
    try {
      const response = await multiTemplateGeneratorAPI.getGroups()
      if (response.data.success) {
        setGroups(response.data.data)
      }
    } catch (error) {
      console.error('Error fetching groups:', error)
    }
  }

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this item?')) return

    try {
      await multiTemplateGeneratorAPI.delete(id)
      alert('Item deleted successfully')
      fetchItems()
    } catch (error) {
      console.error('Error deleting item:', error)
      alert('Failed to delete item')
    }
  }

  const handleToggleActive = async (id) => {
    try {
      await multiTemplateGeneratorAPI.toggleActive(id)
      fetchItems()
    } catch (error) {
      console.error('Error toggling active status:', error)
      alert('Failed to toggle active status')
    }
  }

  const handleDeleteGroup = async (group) => {
    if (!window.confirm(`Are you sure you want to delete all items in group "${group}"?`)) return

    try {
      await multiTemplateGeneratorAPI.deleteGroup(group)
      alert('Group deleted successfully')
      fetchItems()
      fetchGroups()
    } catch (error) {
      console.error('Error deleting group:', error)
      alert('Failed to delete group')
    }
  }

  const filteredItems = items.filter(item => {
    if (filters.search) {
      const searchLower = filters.search.toLowerCase()
      return (
        item.group.toLowerCase().includes(searchLower) ||
        item.template_id.toLowerCase().includes(searchLower) ||
        item.id.toLowerCase().includes(searchLower)
      )
    }
    return true
  })

  const handleTemplateUpload = async (e) => {
    e.preventDefault()

    if (!templateForm.file) {
      alert(`Please select ${templateType === 'video' ? 'a video' : 'an image'} file`)
      return
    }

    if (!templateForm.templateName.trim()) {
      alert('Please enter a template name')
      return
    }

    if (!templateForm.group.trim()) {
      alert('Please enter a group name')
      return
    }

    if (!templateForm.width || !templateForm.height) {
      alert('Please enter width and height')
      return
    }

    try {
      setUploading(true)
      setUploadProgress(0)
      setUploadStage('Preparing upload...')

      // Create FormData
      const formData = new FormData()
      formData.append('file', templateForm.file)
      formData.append('templateName', templateForm.templateName)
      formData.append('group', templateForm.group)
      formData.append('width', templateForm.width)
      formData.append('height', templateForm.height)
      formData.append('templateType', templateType) // Add template type
      if (templateForm.subcategory.trim()) {
        formData.append('subcategory', templateForm.subcategory)
      }

      // Debug: Log what we're sending
      console.log('📤 Uploading with data:', {
        templateName: templateForm.templateName,
        group: templateForm.group,
        subcategory: templateForm.subcategory,
        width: templateForm.width,
        height: templateForm.height,
        fileName: templateForm.file.name,
        templateType: templateType
      })

      // Use XMLHttpRequest for progress tracking
      const xhr = new XMLHttpRequest()

      // Track upload progress
      xhr.upload.addEventListener('progress', (e) => {
        if (e.lengthComputable) {
          const percentComplete = (e.loaded / e.total) * 70 // 70% for upload
          setUploadProgress(Math.round(percentComplete))
          setUploadStage(`Uploading... ${Math.round(percentComplete)}%`)
        }
      })

      // Determine the endpoint based on template type
      const endpoint = templateType === 'video' 
        ? '/api/v1/multi-template-generator/add-template'
        : '/api/v1/multi-template-generator/add-image-template'

      // Handle response
      const response = await new Promise((resolve, reject) => {
        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            setUploadProgress(75)
            setUploadStage(`Processing ${templateType}...`)
            resolve({
              ok: xhr.status >= 200 && xhr.status < 300,
              status: xhr.status,
              json: () => Promise.resolve(JSON.parse(xhr.responseText))
            })
          } else {
            reject(new Error(`HTTP ${xhr.status}: ${xhr.statusText}`))
          }
        }

        xhr.onerror = () => reject(new Error('Network error'))

        xhr.open('POST', endpoint)
        xhr.setRequestHeader('Authorization', `Bearer ${localStorage.getItem('token')}`)
        xhr.send(formData)
      })

      if (response.ok) {
        const result = await response.json()
        setUploadProgress(100)
        setUploadStage('Complete!')
        
        const templatesAdded = result.data?.templates_created || 0
        alert(`Success! ${templatesAdded} new template${templatesAdded !== 1 ? 's' : ''} added.`)
        
        // Reset form
        setShowAddTemplateDialog(false)
        setTemplateForm({
          templateName: '',
          group: '',
          subcategory: '',
          width: '',
          height: '',
          file: null,
          detectedAttributes: null
        })
        
        // Refresh items list
        fetchItems()
        fetchGroups()
      } else {
        const errorData = await response.json()
        alert(`Upload failed: ${errorData.message || 'Unknown error'}`)
      }
    } catch (error) {
      console.error('Error uploading template:', error)
      alert('Failed to upload template: ' + error.message)
    } finally {
      setUploading(false)
      setUploadProgress(0)
      setUploadStage('')
    }
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Multi Template Generator</h1>
            <p className="text-gray-600 mt-1">Manage template groups and assignments</p>
          </div>
          <div className="flex gap-3">
            <button
              onClick={() => {
                setTemplateType('image')
                setShowAddTemplateDialog(true)
              }}
              className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
            >
              <Plus className="w-5 h-5" />
              Add Image Template
            </button>
            <button
              onClick={() => {
                setTemplateType('video')
                setShowAddTemplateDialog(true)
              }}
              className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
            >
              <Plus className="w-5 h-5" />
              Add Video Template
            </button>
            <button
              onClick={() => navigate('/dashboard/multi-template-generator/create')}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <Plus className="w-5 h-5" />
              Add New
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Search
              </label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  type="text"
                  value={filters.search}
                  onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                  placeholder="Search by group, template, or ID..."
                  className="pl-10 w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Group
              </label>
              <select
                value={filters.group}
                onChange={(e) => setFilters(prev => ({ ...prev, group: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">All Groups</option>
                {groups.map(group => (
                  <option key={group} value={group}>{group}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Status
              </label>
              <select
                value={filters.is_active === null ? '' : filters.is_active.toString()}
                onChange={(e) => setFilters(prev => ({ 
                  ...prev, 
                  is_active: e.target.value === '' ? null : e.target.value === 'true' 
                }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">All Status</option>
                <option value="true">Active</option>
                <option value="false">Inactive</option>
              </select>
            </div>

            <div className="flex items-end">
              <button
                onClick={() => {
                  setFilters({ group: '', is_active: null, search: '' })
                  setPagination(prev => ({ ...prev, page: 1 }))
                }}
                className="w-full px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Clear Filters
              </button>
            </div>
          </div>
        </div>

        {/* Items Table */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          {loading ? (
            <div className="p-8 text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
              <p className="text-gray-600 mt-4">Loading...</p>
            </div>
          ) : filteredItems.length === 0 ? (
            <div className="p-8 text-center">
              <p className="text-gray-600">No items found</p>
            </div>
          ) : (
            <>
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Group
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Template ID
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Category
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Sub Category
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Created
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredItems.map((item) => (
                    <tr key={item.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">{item.group}</div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-900 font-mono text-xs">
                          {item.template_id}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {item.category?.name || '-'}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {item.sub_category?.name || '-'}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 py-1 text-xs rounded-full ${
                          item.is_active 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-red-100 text-red-800'
                        }`}>
                          {item.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {toUtcDate(item.created_at).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata', day: '2-digit', month: 'short', year: 'numeric' })}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => window.open(`/editor/${item.template_id}`, '_blank')}
                            className="p-1 text-purple-600 rounded hover:bg-purple-50"
                            title="Open in Editor"
                          >
                            <ExternalLink className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleToggleActive(item.id)}
                            className={`p-1 rounded hover:bg-gray-100 ${
                              item.is_active ? 'text-green-600' : 'text-gray-400'
                            }`}
                            title={item.is_active ? 'Deactivate' : 'Activate'}
                          >
                            {item.is_active ? <Power className="w-4 h-4" /> : <PowerOff className="w-4 h-4" />}
                          </button>
                          <button
                            onClick={() => navigate(`/dashboard/multi-template-generator/edit/${item.id}`)}
                            className="p-1 text-blue-600 rounded hover:bg-blue-50"
                            title="Edit"
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(item.id)}
                            className="p-1 text-red-600 rounded hover:bg-red-50"
                            title="Delete"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {/* Pagination */}
              {pagination.totalPages > 1 && (
                <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
                  <div className="text-sm text-gray-700">
                    Showing {((pagination.page - 1) * pagination.limit) + 1} to {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total} results
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                      disabled={!pagination.hasPrev}
                      className="px-3 py-1 border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Previous
                    </button>
                    <button
                      onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                      disabled={!pagination.hasNext}
                      className="px-3 py-1 border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Next
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Groups Management */}
        {groups.length > 0 && (
          <div className="mt-6 bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Groups</h2>
            <div className="flex flex-wrap gap-2">
              {groups.map(group => (
                <div key={group} className="flex items-center gap-2 px-3 py-2 bg-gray-100 rounded-lg">
                  <span className="text-sm font-medium text-gray-700">{group}</span>
                  <button
                    onClick={() => handleDeleteGroup(group)}
                    className="text-red-600 hover:text-red-800"
                    title="Delete group"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Add Template Dialog */}
      {showAddTemplateDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="text-lg font-semibold">
                Add {templateType === 'video' ? 'Video' : 'Image'} Template
              </h3>
              <button
                onClick={() => {
                  setShowAddTemplateDialog(false)
                  setTemplateForm({
                    templateName: '',
                    group: '',
                    subcategory: '',
                    width: '',
                    height: '',
                    file: null,
                    detectedAttributes: null
                  })
                }}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleTemplateUpload} className="p-4 space-y-4">
              {/* Template Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Template Name *
                </label>
                <input
                  type="text"
                  value={templateForm.templateName}
                  onChange={(e) => setTemplateForm(prev => ({ ...prev, templateName: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="e.g., Birthday Wishes, New Year Greetings"
                  required
                />
              </div>

              {/* Group Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Group Name *
                </label>
                <input
                  type="text"
                  value={templateForm.group}
                  onChange={(e) => setTemplateForm(prev => ({ ...prev, group: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="e.g., social-media, banners"
                  required
                />
              </div>

              {/* Subcategory */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Subcategory
                </label>
                <input
                  type="text"
                  value={templateForm.subcategory}
                  onChange={(e) => setTemplateForm(prev => ({ ...prev, subcategory: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="e.g., birthday, anniversary"
                />
              </div>

              {/* Width and Height */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Width (px) *
                  </label>
                  <input
                    type="number"
                    value={templateForm.width}
                    onChange={(e) => setTemplateForm(prev => ({ ...prev, width: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="1920"
                    min="1"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Height (px) *
                  </label>
                  <input
                    type="number"
                    value={templateForm.height}
                    onChange={(e) => setTemplateForm(prev => ({ ...prev, height: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="1080"
                    min="1"
                    required
                  />
                </div>
              </div>

              {/* File Upload - Video or Image based on templateType */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {templateType === 'video' ? 'Video' : 'Image'} File *
                </label>
                <input
                  type="file"
                  accept={templateType === 'video' ? 'video/*' : 'image/*'}
                  onChange={(e) => {
                    const file = e.target.files[0]
                    if (!file) return

                    // Check file size (200MB limit for video, 50MB for image)
                    const maxSize = templateType === 'video' ? 200 * 1024 * 1024 : 50 * 1024 * 1024
                    const maxSizeLabel = templateType === 'video' ? '200MB' : '50MB'
                    if (file.size > maxSize) {
                      alert(`File size too large. Maximum allowed size is ${maxSizeLabel}. Your file is ${(file.size / (1024 * 1024)).toFixed(1)}MB.`)
                      e.target.value = '' // Clear the input
                      return
                    }

                    if (templateType === 'video') {
                      // Extract video attributes using HTML5 video element
                      const video = document.createElement('video')
                      video.preload = 'metadata'

                      video.onloadedmetadata = () => {
                        console.log('📹 Video metadata loaded:', {
                          duration: video.duration,
                          width: video.videoWidth,
                          height: video.videoHeight
                        })

                        setTemplateForm(prev => ({
                          ...prev,
                          file,
                          // Auto-fill width and height if not set
                          width: prev.width || video.videoWidth.toString(),
                          height: prev.height || video.videoHeight.toString(),
                          // Store detected attributes for display
                          detectedAttributes: {
                            duration: video.duration,
                            width: video.videoWidth,
                            height: video.videoHeight,
                            size: file.size,
                            type: file.type
                          }
                        }))

                        // Clean up
                        URL.revokeObjectURL(video.src)
                      }

                      video.onerror = () => {
                        console.warn('⚠️ Could not load video metadata')
                        setTemplateForm(prev => ({
                          ...prev,
                          file,
                          detectedAttributes: {
                            size: file.size,
                            type: file.type
                          }
                        }))
                        URL.revokeObjectURL(video.src)
                      }

                      video.src = URL.createObjectURL(file)
                    } else {
                      // Extract image attributes using HTML5 Image element
                      const img = new Image()

                      img.onload = () => {
                        console.log('🖼️ Image metadata loaded:', {
                          width: img.naturalWidth,
                          height: img.naturalHeight
                        })

                        setTemplateForm(prev => ({
                          ...prev,
                          file,
                          // Auto-fill width and height if not set
                          width: prev.width || img.naturalWidth.toString(),
                          height: prev.height || img.naturalHeight.toString(),
                          // Store detected attributes for display
                          detectedAttributes: {
                            width: img.naturalWidth,
                            height: img.naturalHeight,
                            size: file.size,
                            type: file.type
                          }
                        }))

                        // Clean up
                        URL.revokeObjectURL(img.src)
                      }

                      img.onerror = () => {
                        console.warn('⚠️ Could not load image metadata')
                        setTemplateForm(prev => ({
                          ...prev,
                          file,
                          detectedAttributes: {
                            size: file.size,
                            type: file.type
                          }
                        }))
                        URL.revokeObjectURL(img.src)
                      }

                      img.src = URL.createObjectURL(file)
                    }
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required
                />

                {/* Display detected file attributes */}
                {templateForm.detectedAttributes && (
                  <div className={`mt-2 p-3 ${templateType === 'video' ? 'bg-blue-50 border-blue-200' : 'bg-purple-50 border-purple-200'} border rounded-lg`}>
                    <p className={`font-medium ${templateType === 'video' ? 'text-blue-900' : 'text-purple-900'} mb-2`}>
                      {templateType === 'video' ? '📹 Video' : '🖼️ Image'} Information
                    </p>
                    <div className={`grid grid-cols-2 gap-2 text-sm ${templateType === 'video' ? 'text-blue-800' : 'text-purple-800'}`}>
                      <div>Size: {(templateForm.detectedAttributes.size / (1024 * 1024)).toFixed(1)} MB</div>
                      <div>Type: {templateForm.detectedAttributes.type}</div>
                      {templateForm.detectedAttributes.duration && (
                        <div>Duration: {templateForm.detectedAttributes.duration.toFixed(1)}s</div>
                      )}
                      {templateForm.detectedAttributes.width && templateForm.detectedAttributes.height && (
                        <div>Resolution: {templateForm.detectedAttributes.width} × {templateForm.detectedAttributes.height}</div>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Upload Progress */}
              {uploading && (
                <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-gray-700">{uploadStage}</span>
                    <span className="text-sm text-gray-500">{uploadProgress}%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className={`${templateType === 'video' ? 'bg-blue-600' : 'bg-purple-600'} h-2 rounded-full transition-all duration-300 ease-out`}
                      style={{ width: `${uploadProgress}%` }}
                    ></div>
                  </div>
                  <div className="mt-2 text-xs text-gray-500">
                    {uploadProgress < 70 ? `Uploading ${templateType} file...` :
                     uploadProgress < 90 ? `Processing ${templateType}...` :
                     'Finalizing upload...'}
                  </div>
                </div>
              )}

              <div className="flex items-center justify-end gap-2 pt-4">
                <button
                  type="button"
                  onClick={() => setShowAddTemplateDialog(false)}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
                  disabled={uploading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={uploading || !templateForm.file}
                  className={`px-4 py-2 ${templateType === 'video' ? 'bg-green-600 hover:bg-green-700' : 'bg-purple-600 hover:bg-purple-700'} text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed`}
                >
                  {uploading ? (uploadStage || 'Processing...') : `Upload ${templateType === 'video' ? 'Video' : 'Image'} Template`}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}

export default MultiTemplateGeneratorPage
